/**
 * 「よく使われる事例」の絞り込みロジック (純関数)。
 *
 * ## なぜ画面から切り出すか
 *
 * 今回のスコープはタグ絞り込みのみで、**文字列検索は含めない**。
 * ただし将来の検索追加時に画面 (`UseCasePanel`) を書き換えずに済むよう、
 * 条件は最初から `SsmlUseCaseFilter` という **オブジェクト 1 個**で受け取る。
 * 検索を足すときは
 *
 *   1. `SsmlUseCaseFilter` に `keyword?: string` を追加
 *   2. 本ファイルの `matchesFilter` に語のマッチ判定を追加
 *   3. 画面に入力欄を 1 つ足して `filter` へ渡す
 *
 * の 3 手で済み、絞り込みの判定は本ファイルに閉じたままになる。
 * (判定を画面の JSX に埋め込むと、検索追加時に描画とロジックを
 *  同時に触ることになり、テストも書きづらくなる)
 *
 * ## タグの AND / OR
 *
 * **OR (いずれかを含む)** とする。利用者は「駅名 か 方面 の話」を
 * ざっくり探すため、AND だと 0 件になりやすく入口として機能しない。
 * 絞り込み後も 0 件になり得る (タグ組み合わせ次第) ため、
 * 呼び出し側は空配列を必ず扱う。
 */
import type { SsmlTuningKnowledge, SsmlUseCase } from './types';
import type { SsmlUseCaseTag } from './useCaseTags';

/**
 * 絞り込み条件。
 *
 * ⚠️ 将来 `keyword` を足す前提で **オブジェクトで受ける**。
 *   引数を増やす形にすると呼び出し側全てを直す必要が出る。
 */
export interface SsmlUseCaseFilter {
  /** 選択中のタグ (空配列 = 絞り込みなし = 全件表示) */
  tags: readonly SsmlUseCaseTag[];
}

/** 絞り込みなし (全件表示) の条件。初期状態として使う */
export const EMPTY_SSML_USE_CASE_FILTER: SsmlUseCaseFilter = { tags: [] };

/**
 * 1 件がタグ条件に合致するか判定する。
 *
 * タグ未選択は「絞り込みなし」なので **常に true** を返す
 * (「タグが無い事例だけ残る」という誤動作を避ける)。
 */
function matchesFilter(
  item: { readonly tags: readonly SsmlUseCaseTag[] },
  filter: SsmlUseCaseFilter,
): boolean {
  if (filter.tags.length === 0) return true;
  return item.tags.some((tag) => filter.tags.includes(tag));
}

/** 事例一覧を条件で絞り込む (元配列は変更しない) */
export function filterUseCases(
  useCases: readonly SsmlUseCase[],
  filter: SsmlUseCaseFilter,
): readonly SsmlUseCase[] {
  return useCases.filter((useCase) => matchesFilter(useCase, filter));
}

/**
 * ナレッジ (現場で調べた結果) を条件で絞り込む。
 *
 * ナレッジも事例と同じタグを持つため、同じ条件で絞り込めるようにしておく
 * (タグを選んだのにナレッジ側が全件残っていると、絞り込んだ実感が損なわれる)。
 *
 * ⚠️ 状態 (`status`) では **絞り込まない**。未解決を隠せる仕組みを作ると
 *   「解決済みだけ見る」運用に流れ、未解決を残す目的
 *   (同じ調査の繰り返しを防ぐ) が失われる。状態は各カードのバッジで示す。
 */
export function filterKnowledge(
  knowledge: readonly SsmlTuningKnowledge[],
  filter: SsmlUseCaseFilter,
): readonly SsmlTuningKnowledge[] {
  return knowledge.filter((item) => matchesFilter(item, filter));
}

/**
 * タグ選択のトグル (選択中なら外し、未選択なら足す)。
 *
 * 画面側で `includes` 判定と配列操作を書くと選択状態の扱いが散らばるため、
 * ここに集約する。返り値は新しい配列 (React の state 更新に使える)。
 */
export function toggleUseCaseTag(
  selected: readonly SsmlUseCaseTag[],
  tag: SsmlUseCaseTag,
): readonly SsmlUseCaseTag[] {
  return selected.includes(tag) ? selected.filter((t) => t !== tag) : [...selected, tag];
}

/**
 * 事例とナレッジに実際に使われているタグを数える。
 *
 * ⚠️ 0 件のタグをボタンに出すと「押しても何も出ない」体験になるため、
 *   画面側は本関数の結果を使って **件数 0 のタグを出さない**。
 */
export function countUseCaseTags(
  useCases: readonly SsmlUseCase[],
  knowledge: readonly SsmlTuningKnowledge[],
): ReadonlyMap<SsmlUseCaseTag, number> {
  const counts = new Map<SsmlUseCaseTag, number>();
  [...useCases, ...knowledge].forEach((item) => {
    item.tags.forEach((tag) => {
      counts.set(tag, (counts.get(tag) ?? 0) + 1);
    });
  });
  return counts;
}
